import React, { useEffect, useState } from "react";
import {
  Modal,
  Input,
  DatePicker,
  TimePicker,
  Switch,
  Select,
  Tag,
  Space,
  Button,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { TASK_COLORS } from "../utils/colors.ts";

/* ─────────── типы ─────────── */
export type RepeatType = 0 | 1 | 2 | 3 | 4;           // none/day/week/month/year

export interface TaskDraft {
  id?: number;
  title : string;
  note  : string | null;
  time  : Dayjs;               // объединённая дата-время
  color : string;              // #RRGGBB
  repeat: RepeatType;
  until?: Dayjs | null;        // дата окончания повторов
  notify: boolean;
  done  : boolean;
}

/* ─────────── компонент ─────────── */
interface Props {
  open: boolean;
  initial?: TaskDraft | null;
  onSave:   (t: TaskDraft) => void;
  onCancel: () => void;
}

const TaskEditorModal: React.FC<Props> = ({ open, initial, onSave, onCancel }) => {
  /* локальное состояние формы */
  const [title,  setTitle]  = useState(initial?.title ?? "");
  const [note,   setNote]   = useState<string>(initial?.note ?? "");
  const [date,   setDate]   = useState<Dayjs>(initial?.time ?? dayjs());
  const [time,   setTime]   = useState<Dayjs>(initial?.time ?? dayjs());
  const [color,  setColor]  = useState<string>(initial?.color ?? TASK_COLORS[0]);
  const [repeat, setRepeat] = useState<RepeatType>(initial?.repeat ?? 0);
  const [until,  setUntil]  = useState<Dayjs | null>(initial?.until ?? null);
  const [notify, setNotify] = useState<boolean>(initial?.notify ?? false);

  /* при открытии / смене initial сбрасываем форму */
  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title ?? "");
    setNote(initial?.note ?? "");
    setDate(initial?.time ?? dayjs());
    setTime(initial?.time ?? dayjs());
    setColor(initial?.color ?? TASK_COLORS[0]);
    setRepeat(initial?.repeat ?? 0);
    setUntil(initial?.until ?? null);
    setNotify(initial?.notify ?? false);
  }, [open, initial]);

  /* сохранить */
  const handleOk = () => {
    if (!title.trim()) return;
    const merged = date
      .hour(time.hour())
      .minute(time.minute())
      .second(0)
      .millisecond(0);

    onSave({
      id    : initial?.id,
      title : title.trim(),
      note  : note.trim() ? note.trim() : null,
      time  : merged,
      color,
      repeat,
      until,
      notify,
      done  : initial?.done ?? false,
    });
  };

  /* UI */
  return (
    <Modal
      open={open}
      title={initial ? "Редактирование" : "Новая задача"}
      onOk={handleOk}
      okText="Сохранить"
      cancelText="Отмена"
      onCancel={onCancel}
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Input
          placeholder="Заголовок"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <Input.TextArea
          rows={2}
          placeholder="Описание"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {/* дата + время */}
        <Space>
          <DatePicker
            value={date}
            onChange={(d) => d && setDate(d)}
            format="DD.MM.YYYY"
          />
          <TimePicker
            value={time}
            onChange={(t) => t && setTime(t)}
            format="HH:mm"
          />
        </Space>

        {/* цвет */}
        <div>
          Цвет:&nbsp;
          {TASK_COLORS.map((c) => (
            <Tag
              key={c}
              color={c}
              style={{
                cursor: "pointer",
                border: c === color ? "2px solid #fff" : "2px solid transparent",
              }}
              onClick={() => setColor(c)}
            />
          ))}
        </div>

        {/* повтор */}
        <Space style={{ width: "100%" }}>
          <span>Повтор:</span>
          <Select<RepeatType>
            style={{ flex: 1 }}
            value={repeat}
            onChange={setRepeat}
            options={[
              { value: 0, label: "Не повторять" },
              { value: 1, label: "Каждый день" },
              { value: 2, label: "Каждую неделю" },
              { value: 3, label: "Каждый месяц" },
              { value: 4, label: "Каждый год" },
            ]}
          />
        </Space>

        {repeat !== 0 && (
          <Space>
            <span>До:</span>
            <DatePicker
              value={until ?? undefined}
              onChange={(d) => setUntil(d ?? null)}
              disabledDate={(d) => d.isBefore(date, "day")}
            />
            {until && (
              <Button size="small" onClick={() => setUntil(null)}>
                очистить
              </Button>
            )}
          </Space>
        )}

        {/* уведомление */}
        <Space>
          <span>Уведомление</span>
          <Switch checked={notify} onChange={setNotify} />
        </Space>
      </Space>
    </Modal>
  );
};

export default TaskEditorModal;
